<script setup lang="ts">
  // Historic caja closes migrated to the V2 design system: date range +
  // caja filter, expandable close cards with movements modal and reprint.
  import { computed, getCurrentInstance, ref, watch } from 'vue';
  import CajasService from '@/api/services/cajas.service';
  import PosUiEmptyState from '@/components/pos-v2/ui/PosUiEmptyState.vue';
  import V2ReportPage from '@/components/v2/stats/V2ReportPage.vue';
  import V2StatsDateRange from '@/components/v2/stats/V2StatsDateRange.vue';
  import V2StatsScopeNote from '@/components/v2/stats/V2StatsScopeNote.vue';
  import { useUi } from '@/composition/use-ui';
  import { IStatsDateRange } from '@/domain/stats/stats-date-range';
  import { ICajaMovement } from '@/interfaces/ICajaMovement';
  import { useRestaurantStore } from '@/stores/restaurant.store';
  import V2PageCajasHistoricMovement from './V2PageCajasHistoricMovement.vue';

  const ui = useUi();
  const restaurantStore = useRestaurantStore();
  const proxy = getCurrentInstance()!.proxy;

  function i18nT(key: string): string {
    return String(proxy.$t(key));
  }

  const range = ref<IStatsDateRange | null>(null);
  const cajaId = ref<number | null>(null);
  const showExpandedResumes = ref(true);

  const cajaOptions = computed(() => [
    { text: i18nT('v2Stats.cajas.allCajas'), value: null },
    ...restaurantStore.cajas.map((c) => ({ text: c.name, value: c.id })),
  ]);

  function onRangeChange(value: IStatsDateRange) {
    range.value = value;
    load();
  }

  function onCajaChange(event: Event) {
    const raw = (event.target as HTMLSelectElement).value;
    cajaId.value = raw === '' ? null : Number(raw);
  }

  watch(cajaId, () => {
    load();
  });

  const historic = ref<ICajaMovement[] | null>(null);

  let requestGeneration = 0;

  async function load() {
    const restaurant = restaurantStore.restaurant;
    const branch = restaurantStore.selectedBranch;
    if (!restaurant || !branch || !range.value) return;

    const generation = ++requestGeneration;
    historic.value = null;

    // Close movements are instant-stamped; query the inclusive local window.
    const dateTo = new Date(range.value.to);
    dateTo.setHours(23, 59, 59, 999);

    try {
      const result = await CajasService.getHistoric({
        restaurantId: restaurant.id,
        branchId: branch.id,
        dateFrom: range.value.from,
        dateTo,
        cajaId: cajaId.value ?? undefined,
      });
      if (generation !== requestGeneration) return;
      historic.value = result;
    } catch (error) {
      if (generation !== requestGeneration) return;
      ui.serverError(error, i18nT('v2Stats.cajas.loadError'));
    }
  }

  const filteredMovements = computed(() => {
    if (!historic.value) return null;
    return historic.value.filter((m) => m.data);
  });
</script>

<template>
  <V2ReportPage
    :title="String($t('v2Stats.cajas.title'))"
    :subtitle="restaurantStore.selectedBranch?.name"
  >
    <template #range>
      <V2StatsDateRange
        v-if="restaurantStore.restaurant && restaurantStore.selectedBranch"
        :restaurant-id="restaurantStore.restaurant.id"
        :branch-id="restaurantStore.selectedBranch.id"
        storage-key="cajas"
        @change="onRangeChange"
      />
    </template>

    <template #note>
      <V2StatsScopeNote
        storage-key="cajas"
        :text="String($t('statsNote.cajas'))"
      />
    </template>

    <div class="cajas-toolbar">
      <label class="caja-filter">
        <span class="caja-filter-label">{{ $t('v2Stats.cajas.caja') }}</span>
        <select
          class="caja-filter-select"
          :value="cajaId === null ? '' : String(cajaId)"
          @change="onCajaChange"
        >
          <option
            v-for="option in cajaOptions"
            :key="option.value === null ? 'all' : option.value"
            :value="option.value === null ? '' : String(option.value)"
          >
            {{ option.text }}
          </option>
        </select>
      </label>

      <button
        type="button"
        class="expand-toggle"
        :class="{ 'is-active': showExpandedResumes }"
        @click="showExpandedResumes = !showExpandedResumes"
      >
        {{ $t('v2Stats.cajas.expandedResumes') }}
      </button>
    </div>

    <div class="cajas-list">
      <template v-if="!filteredMovements">
        <div class="cajas-skeleton" />
        <div class="cajas-skeleton" />
      </template>
      <div v-else-if="!filteredMovements.length" class="cajas-empty">
        <PosUiEmptyState
          icon="cash-register"
          :title="String($t('v2Stats.cajas.empty'))"
        />
      </div>
      <template v-else>
        <V2PageCajasHistoricMovement
          v-for="m in filteredMovements"
          :key="m.id"
          :movement="m"
          :dense="!showExpandedResumes"
        />
      </template>
    </div>
  </V2ReportPage>
</template>

<style scoped lang="scss">
  .cajas-toolbar {
    @apply tw-flex tw-flex-wrap tw-items-center tw-justify-between;

    .caja-filter {
      @apply tw-mb-2 tw-mr-3 tw-inline-flex tw-items-center;

      .caja-filter-label {
        @apply tw-mr-1.5 tw-text-xs tw-font-medium tw-text-neutral-500;
      }

      .caja-filter-select {
        @apply tw-h-11 tw-min-w-[12rem] tw-rounded-lg tw-border tw-border-solid tw-border-neutral-300 tw-bg-white tw-px-2.5 tw-text-sm tw-text-neutral-900;

        &:focus {
          @apply tw-border-orange-500 tw-outline-none tw-ring-2 tw-ring-orange-500/40;
        }
      }
    }

    .expand-toggle {
      @apply tw-mb-2 tw-h-11 tw-whitespace-nowrap tw-rounded-lg tw-border tw-border-solid tw-border-neutral-200 tw-bg-white tw-px-3 tw-text-sm tw-font-medium tw-text-neutral-500 tw-transition-colors;

      &.is-active {
        @apply tw-border-neutral-900 tw-bg-neutral-900 tw-text-white;
      }

      &:focus-visible {
        @apply tw-outline-none tw-ring-2 tw-ring-orange-500/40;
      }
    }
  }

  .cajas-list {
    & > * + * {
      @apply tw-mt-3;
    }

    .cajas-skeleton {
      @apply tw-h-24 tw-animate-pulse tw-rounded-xl tw-bg-neutral-200;
    }

    .cajas-empty {
      @apply tw-rounded-xl tw-border tw-border-solid tw-border-neutral-200 tw-bg-white;
    }
  }
</style>
